// Shared UI state - viewer instance accessible across UI modules

import type { GardenViewer } from '@/GardenViewer'
import type { LeftPanel } from './LeftPanel'
import type { ZoneType, PinType } from '@/types/zone'
import type { Product } from '@/types/garden'

let viewer: GardenViewer | null = null
let leftPanelInstance: LeftPanel | null = null
let isStartingViewer = false

// Tool mode: 'hand' (pan only), 'pointer' (selection enabled), or 'ruler' (measurement)
export type ToolMode = 'hand' | 'pointer' | 'ruler'
let toolMode: ToolMode = 'pointer'  // Default to pointer (selection enabled)

export function getViewer(): GardenViewer | null {
  return viewer
}

export function setViewer(v: GardenViewer | null): void {
  viewer = v
}

export function getLeftPanel(): LeftPanel | null {
  return leftPanelInstance
}

export function setLeftPanel(panel: LeftPanel | null): void {
  leftPanelInstance = panel
}

export function isViewerStarting(): boolean {
  return isStartingViewer
}

export function setViewerStarting(starting: boolean): void {
  isStartingViewer = starting
}

export function getToolMode(): ToolMode {
  return toolMode
}

export function setToolMode(mode: ToolMode): void {
  toolMode = mode
}

// Selected zone type for drawing tools (metadata tab)
let selectedZoneType: ZoneType | null = null

export function getSelectedZoneType(): ZoneType | null {
  return selectedZoneType
}

export function setSelectedZoneType(type: ZoneType | null): void {
  selectedZoneType = type
  // Mutual exclusion: selecting a zone clears pin and product selection
  if (type !== null) {
    selectedPinType = null
    selectedProduct = null
    selectedProductLayer = null
  }
  notifyPlacementListeners()
}

// Selected pin type for POI placement (metadata tab)
let selectedPinType: PinType | null = null

export function getSelectedPinType(): PinType | null {
  return selectedPinType
}

export function setSelectedPinType(type: PinType | null): void {
  selectedPinType = type
  // Mutual exclusion: selecting a pin clears zone and product selection
  if (type !== null) {
    selectedZoneType = null
    selectedProduct = null
    selectedProductLayer = null
  }
  notifyPlacementListeners()
}

// Placement mode: single (place one, return to select), continuous (keep placing), or linear (place row)
export type PlacementMode = 'single' | 'continuous' | 'linear'
let placementMode: PlacementMode = 'single'

// State change listeners for placement state (mode + product changes)
type PlacementStateListener = () => void
const placementListeners: PlacementStateListener[] = []

export function onPlacementStateChange(cb: PlacementStateListener): () => void {
  placementListeners.push(cb)
  return () => {
    const idx = placementListeners.indexOf(cb)
    if (idx >= 0) placementListeners.splice(idx, 1)
  }
}

function notifyPlacementListeners(): void {
  placementListeners.forEach(cb => cb())
}

export function getPlacementMode(): PlacementMode {
  return placementMode
}

export function setPlacementMode(mode: PlacementMode): void {
  placementMode = mode
  notifyPlacementListeners()
}

/**
 * Stop continuous placement: reset mode to single, clear product selection.
 * Called by banner close, Esc key, mode switch, and view mode switch.
 */
export function stopContinuousPlacement(): void {
  placementMode = 'single'
  selectedProduct = null
  selectedProductLayer = null
  notifyPlacementListeners()
}

// Selected product for placement tools (ground/architecture/plants tabs)
let selectedProduct: Product | null = null
let selectedProductLayer: import('@/types/garden').Layer | null = null

export function getSelectedProduct(): Product | null {
  return selectedProduct
}

export function getSelectedProductLayer(): import('@/types/garden').Layer | null {
  return selectedProductLayer
}

export function setSelectedProduct(product: Product | null, layer?: import('@/types/garden').Layer): void {
  selectedProduct = product
  selectedProductLayer = product ? (layer ?? null) : null
  // Mutual exclusion: selecting a product clears zone and pin selection
  if (product !== null) {
    selectedZoneType = null
    selectedPinType = null
    // Preload GLB model / billboard texture so placement is instantaneous
    const viewer = getViewer()
    if (viewer && product.id) {
      viewer.ensureProductCached(product)
      viewer.preloadProductModel(product.id)
    }
  }
  notifyPlacementListeners()
}
